/**
 * Flat story list sorted by priority with drag-drop reordering
 * Story 198: View and filter stories in backlog
 *
 * Uses @atlaskit/pragmatic-drag-and-drop to match StoryNode's drag implementation.
 */

import { useMemo, useEffect } from 'react';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { StoryNode, isStoryDragData } from './StoryNode';
import { useReorderStory } from '../../hooks/useStoryMutations';
import type { StoryListItem } from '../../hooks/useStories';
import './StoryBacklog.css';

interface StoryBacklogProps {
  stories: StoryListItem[];
  projectSlug: string;
  selectedStoryId: string | null;
  onSelectStory: (storyExternalId: string) => void;
  onCreateStory?: () => void;
}

export function StoryBacklog({
  stories,
  projectSlug,
  selectedStoryId,
  onSelectStory,
  onCreateStory,
}: StoryBacklogProps) {
  const reorderStory = useReorderStory();

  // Sort by priority ascending (lower priority = higher in list)
  const sortedStories = useMemo(
    () => [...stories].sort((a, b) => a.priority - b.priority),
    [stories]
  );

  // Monitor for story drops and handle reordering
  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => isStoryDragData(source.data),
      onDrop: ({ source, location }) => {
        const destination = location.current.dropTargets[0];
        if (!destination) return;

        const sourceData = source.data;
        if (!isStoryDragData(sourceData)) return;

        const destData = destination.data as { type: string; storyId?: string };

        // Only handle drops on other stories (not epics)
        if (destData.type !== 'story' || !destData.storyId) return;

        const targetStoryId = destData.storyId;
        const edge = extractClosestEdge(destination.data);

        // Find source and target story positions
        const sourceStory = sortedStories.find(s => s.external_id === sourceData.storyId);
        const targetIndex = sortedStories.findIndex(s => s.external_id === targetStoryId);

        if (!sourceStory || targetIndex === -1) return;

        // Calculate priority based on edge (top = insert before, bottom = insert after)
        let targetPriority: number;
        if (edge === 'top') {
          // Insert before target
          if (targetIndex === 0) {
            targetPriority = sortedStories[0].priority - 1000;
          } else {
            const prevPriority = sortedStories[targetIndex - 1].priority;
            const currPriority = sortedStories[targetIndex].priority;
            targetPriority = Math.floor((prevPriority + currPriority) / 2);
          }
        } else {
          // Insert after target (bottom edge or default)
          if (targetIndex === sortedStories.length - 1) {
            targetPriority = sortedStories[targetIndex].priority + 1000;
          } else {
            const currPriority = sortedStories[targetIndex].priority;
            const nextPriority = sortedStories[targetIndex + 1].priority;
            targetPriority = Math.floor((currPriority + nextPriority) / 2);
          }
        }

        // Preserve epic assignment when reordering in backlog view
        reorderStory.mutate({
          projectSlug,
          storyExternalId: sourceStory.external_id,
          targetEpic: sourceStory.epic_slug,
          targetPriority,
        });
      },
    });
  }, [projectSlug, sortedStories, reorderStory]);

  if (sortedStories.length === 0) {
    return (
      <div className="story-backlog__empty">
        {onCreateStory ? (
          <button
            type="button"
            className="story-backlog__create-button"
            onClick={onCreateStory}
          >
            + Create New Story
          </button>
        ) : (
          'No stories found'
        )}
      </div>
    );
  }

  return (
    <div className="story-backlog">
      {sortedStories.map((story) => (
        <StoryNode
          key={story.id}
          story={story}
          projectSlug={projectSlug}
          isSelected={story.external_id === selectedStoryId}
          onSelect={() => onSelectStory(story.external_id)}
          draggable
        />
      ))}
    </div>
  );
}
